
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, FolderOpen } from "lucide-react";
import { FilePickerModal } from "./FilePickerModal";
import { EditablePathInput } from "./components/EditablePathInput";

interface EnhancedUploadAreaProps {
  label: string;
  onFileUpload: (file: File) => void;
  onFileSelect?: (bucketName: string, fileName: string) => void;
  onPathChange?: (path: string) => void;
  uploading: boolean;
  acceptedTypes: string[];
  allowedBuckets?: string[];
  showBrowseOption?: boolean;
  showPathInput?: boolean;
  currentPath?: string;
}

export const EnhancedUploadArea = ({
  label,
  onFileUpload,
  onFileSelect,
  onPathChange,
  uploading,
  acceptedTypes,
  allowedBuckets,
  showBrowseOption = true,
  showPathInput = true,
  currentPath = "",
}: EnhancedUploadAreaProps) => {
  const [dragActive, setDragActive] = useState(false);
  const [showFilePicker, setShowFilePicker] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (bucketName: string, fileName: string) => {
    onFileSelect?.(bucketName, fileName);
  };

  const handlePathChange = (path: string) => {
    onPathChange?.(path);
  };

  return (
    <div className="space-y-4">
      {showPathInput && onPathChange && (
        <EditablePathInput
          label="File Path"
          currentPath={currentPath}
          onPathChange={handlePathChange}
          placeholder="Enter storage path or external URL"
          disabled={uploading}
        />
      )}
      
      <div>
        <Label>{label}</Label>
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors mt-2 ${
            dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-2">
            Drag and drop your file here, or choose an option below
          </p>
          <p className="text-xs text-gray-500 mb-4">
            Supports: {acceptedTypes.join(', ')}
          </p>
          
          <div className="flex gap-2 justify-center flex-wrap">
            <Input
              type="file"
              onChange={(e) => e.target.files?.[0] && onFileUpload(e.target.files[0])}
              accept={acceptedTypes.join(',')}
              className="hidden"
              id="file-upload"
              disabled={uploading}
            />
            <Label htmlFor="file-upload" className="cursor-pointer">
              <Button type="button" disabled={uploading} asChild>
                <span>Upload New File</span>
              </Button>
            </Label>
            
            {showBrowseOption && onFileSelect && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowFilePicker(true)}
                disabled={uploading}
              >
                <FolderOpen className="h-4 w-4 mr-2" />
                Browse Storage
              </Button>
            )}
          </div>
        </div>
      </div>

      {showFilePicker && (
        <FilePickerModal
          open={showFilePicker}
          onClose={() => setShowFilePicker(false)}
          onFileSelect={handleFileSelect}
          allowedBuckets={allowedBuckets}
        />
      )}
    </div>
  );
};
